/**
 * Pure builder: turn a loaded SamplePlan/Lite payload (+ CalculatorData) into the in-memory tree the editor
 * navigates — Section -> LineItem -> Material -> Rule — with satisfaction rollups stamped on every tier.
 *
 *   const tmpModel = buildSamplePlanModel(samplePlanLite, calculatorData);
 *   // -> { project, sections[], lineItems[], materials[], rules[], plan }   (all plain records)
 *
 * REAL fields are read straight off the payload; everything is guarded so a missing array / sparse field never
 * throws (the live Lite shape varies slightly by deployment). Required/planned counts come from the server (resolvePlimtCounts); satisfaction is derived from created vs required
 * (manual overrides honored). Cert-vs-count: a rule whose computed required count is 0 is "accepted by
 * certificate" (shown as a CD chip, not a 0/N bar); a rule with a real required count shows a samples bar.
 * The pay-item Section number leads each section's tree label (a canonical spec-book identifier).
 */
const fNum = (pValue) => { const tmp = Number(pValue); return Number.isFinite(tmp) ? tmp : 0; };

// Required / planned sample counts come STRAIGHT FROM THE SERVER — never re-derived here. The SamplePlan/Lite
// endpoint runs the authoritative PLIMTCalculator (real placed quantities + temporal/plant data) and stamps
// ComputedRequired/PlannedSampleCount onto each PLIMT; a ManualRequired/PlannedSampleCount >= 0 is an operator
// override that wins. (The old client-side CEIL(qty / (rate*frequency)) ignored placed quantities + plant data and
// was wrong for time/lot-based rules — see PLIMTCalculator on the server.)
const resolvePlimtCounts = (pPLIMT) =>
{
	const fResolve = (pManual, pComputed) => { const tmpManual = Number(pManual); return ((pManual != null) && Number.isFinite(tmpManual) && (tmpManual >= 0)) ? tmpManual : fNum(pComputed); };
	return {
		Required: fResolve(pPLIMT.ManualRequiredSampleCount, pPLIMT.ComputedRequiredSampleCount),
		Planned: fResolve(pPLIMT.ManualPlannedSampleCount, pPLIMT.ComputedPlannedSampleCount),
	};
};
const fStr = (pValue) => ((pValue === undefined || pValue === null) ? '' : String(pValue));

// Derive a pay-item Section code for a line item: prefer an explicit field, else the ItemCode prefix
// (e.g. "601-04-63738" -> "601"), else a catch-all bucket.
const fSectionCode = (pLineItem) =>
{
	if (pLineItem.Section !== undefined && pLineItem.Section !== null && String(pLineItem.Section).trim() !== '') { return String(pLineItem.Section).trim(); }
	if (pLineItem.PayItemSection !== undefined && pLineItem.PayItemSection !== null && String(pLineItem.PayItemSection).trim() !== '') { return String(pLineItem.PayItemSection).trim(); }
	const tmpCode = fStr(pLineItem.ItemCode || pLineItem.Code);
	if (tmpCode.indexOf('-') > 0) { return tmpCode.split('-')[0]; }
	return tmpCode || 'Other';
};
const fSectionName = (pLineItem) => fStr(pLineItem.SectionName || pLineItem.PayItemName || pLineItem.PayItemDescription || '');

const fEmptyRollup = () => ({ SamplesRequired: 0, SamplesCreated: 0, CertCount: 0, CertPending: 0, Rules: 0 });
const fAddRuleToRollup = (pAgg, pRule) =>
{
	pAgg.Rules++;
	if (pRule.Kind === 'count') { pAgg.SamplesRequired += pRule.Required; pAgg.SamplesCreated += pRule.Created; }
	else if (pRule.CertOnFile) { pAgg.CertCount++; } else { pAgg.CertPending++; }
};
// Progress text = the real sample-completion ratio only. (Certificate "on file / pending" counts were dropped: the
// Lite payload has no certificate-received flag, so they were a guess — the nested sample-plan entry count is shown
// instead, via EntryCount.)
const fRollupText = (pAgg) => ((pAgg.SamplesRequired > 0) ? `${pAgg.SamplesCreated}/${pAgg.SamplesRequired} samples` : '');

const buildSamplePlanModel = (pSamplePlan) =>
{
	const tmpPlan = pSamplePlan || {};
	const tmpLineItemsRaw = tmpPlan.LineItems || [];
	const tmpMaterialsRaw = tmpPlan.Materials || [];
	const tmpMLIJRaw = tmpPlan.MaterialLineItemJoins || [];
	const tmpRulesRaw = tmpPlan.ProjectLineItemMaterialTests || [];

	// --- indexes ---
	const tmpMaterialByID = {};
	tmpMaterialsRaw.forEach((pMaterial) => { tmpMaterialByID[pMaterial.IDMaterial] = pMaterial; });
	const tmpTestByID = {};
	(tmpPlan.Tests || []).forEach((pTest) => { tmpTestByID[pTest.IDTest] = pTest; });
	const tmpLineItemByID = {};
	tmpLineItemsRaw.forEach((pLineItem) => { tmpLineItemByID[pLineItem.IDLineItem] = pLineItem; });

	// --- line items + sections ---
	const tmpSections = {};
	const tmpLineItems = {};
	tmpLineItemsRaw.forEach((pLineItem) =>
	{
		const tmpSectionCode = fSectionCode(pLineItem);
		if (!tmpSections[tmpSectionCode])
		{
			tmpSections[tmpSectionCode] = { IDSection: tmpSectionCode, Section: tmpSectionCode, Name: fSectionName(pLineItem) || `Section ${tmpSectionCode}`, LineItemCount: 0 };
		}
		else if (!tmpSections[tmpSectionCode].Name && fSectionName(pLineItem)) { tmpSections[tmpSectionCode].Name = fSectionName(pLineItem); }
		// Label a line item by its pay-item NUMBER (+ name when present) — not the long description. Lead with
		// the number (the canonical line-item identifier), append the proper Name if the payload carries one; the
		// description becomes secondary (subtitle / detail), so the row reads "0010 · Prime Coat" not a paragraph.
		const tmpNumber = fStr(pLineItem.ItemNumber || pLineItem.ItemCode || pLineItem.LineItemNumber || pLineItem.Code);
		let tmpLineName = fStr(pLineItem.Name);
		// The payload Name often already leads with the pay-item number ("0001 Clearing and Grubbing"); strip that
		// leading number so prepending Number below doesn't double it ("0001 · 0001 …" → "0001 · Clearing …").
		if (tmpNumber && (tmpLineName.indexOf(tmpNumber) === 0)) { tmpLineName = tmpLineName.slice(tmpNumber.length).replace(/^[\s·:.\-]+/, ''); }
		const tmpDescription = fStr(pLineItem.Description || pLineItem.ItemDescription);
		tmpLineItems[pLineItem.IDLineItem] =
		{
			IDLineItem: pLineItem.IDLineItem,
			IDSection: tmpSectionCode,
			Number: tmpNumber,
			Code: tmpNumber,
			Description: tmpDescription,
			Name: (tmpNumber && tmpLineName) ? `${tmpNumber}  ·  ${tmpLineName}` : (tmpNumber || tmpLineName || tmpDescription || `Line Item ${pLineItem.IDLineItem}`),
			Units: fStr(pLineItem.Units || pLineItem.UnitsImperial),
			ExpectedQty: fNum(pLineItem.ExpectedQuantity),
			InstalledQty: fNum(pLineItem.InstalledQuantity),
		};
	});

	// --- material nodes: one per (line item, material) pair seen in the BOM (MLIJ) or in the rules ---
	const tmpMaterials = {};
	const fEnsureMaterial = (pIDLineItem, pIDMaterial) =>
	{
		const tmpKey = `${pIDLineItem}:${pIDMaterial}`;
		if (!tmpMaterials[tmpKey])
		{
			const tmpMaterial = tmpMaterialByID[pIDMaterial] || {};
			tmpMaterials[tmpKey] =
			{
				Key: tmpKey, IDLineItem: pIDLineItem, IDMaterial: pIDMaterial,
				Name: fStr(tmpMaterial.Name || tmpMaterial.CatalogName || tmpMaterial.CategoryName) || `Material ${pIDMaterial}`,
				MaterialCode: fStr(tmpMaterial.MaterialCode),
			};
		}
		return tmpMaterials[tmpKey];
	};
	tmpMLIJRaw.forEach((pMLIJ) => { if (tmpLineItems[pMLIJ.IDLineItem]) { fEnsureMaterial(pMLIJ.IDLineItem, pMLIJ.IDMaterial); } });

	// --- rules (one per PLIMT row) + satisfaction via the calculator ---
	const tmpRules = [];
	tmpRulesRaw.forEach((pPLIMT) =>
	{
		if (!tmpLineItems[pPLIMT.IDLineItem]) { return; }   // orphan rule (no line item) — skip
		fEnsureMaterial(pPLIMT.IDLineItem, pPLIMT.IDMaterial);
		const tmpLineItem = tmpLineItemByID[pPLIMT.IDLineItem] || {};
		const tmpMaterial = tmpMaterialByID[pPLIMT.IDMaterial] || null;
		const tmpCounts = resolvePlimtCounts(pPLIMT);
		const tmpRequired = tmpCounts.Required;
		const tmpCreated = fNum(pPLIMT.CreatedSampleCount);
		// Three kinds: 'cert' = accepted by Certificate of Delivery (CertificateRequired) — a real certificate, no lab
		// test. 'perproject' = a TEST sampled once per project (the Frequency 999999 sentinel) — NOT a certificate,
		// even though it isn't an N/M cadence. 'count' = a real sampling cadence (N per unit). CertificateRequired wins
		// when both are set.
		const tmpIsCert = (fNum(pPLIMT.CertificateRequired) === 1);
		const tmpIsPerProject = !tmpIsCert && (fNum(pPLIMT.Frequency) === 999999);
		// The Test entity (when the rule names one via IDTest) carries the test's Hash (e.g. "TR207Ev1") — the
		// canonical test code engineers recognize. Lite omits Tests; the provider fetches + injects them.
		const tmpTest = tmpTestByID[pPLIMT.IDTest] || null;
		const tmpTestHash = tmpTest ? fStr(tmpTest.Hash) : '';
		const tmpSampleType = fStr(pPLIMT.SampleType) || 'ACPT';
		tmpRules.push(
			{
				IDRule: pPLIMT.IDProjectLineItemMaterialTest,
				IDLineItem: pPLIMT.IDLineItem,
				IDMaterial: pPLIMT.IDMaterial,
				MaterialKey: `${pPLIMT.IDLineItem}:${pPLIMT.IDMaterial}`,
				IDTest: pPLIMT.IDTest,
				TestHash: tmpTestHash,
				Title: fStr(pPLIMT.TestDescription || pPLIMT.SampleDescription) || 'Material Acceptance',
				SampleType: tmpSampleType,
				TestLabel: tmpTestHash ? `${tmpTestHash}  ·  ${tmpSampleType}` : tmpSampleType,
				Frequency: fNum(pPLIMT.Frequency),
				Rate: fNum(pPLIMT.Rate) || 1,
				ConversionFactor: fNum(pPLIMT.ConversionFactor) || 1,
				Units: fStr(pPLIMT.Units),
				Intent: fStr(pPLIMT.Intent),
				SamplingInstructions: fStr(pPLIMT.SamplingInstructions),
				Remarks: fStr(pPLIMT.Remarks),
				Kind: tmpIsCert ? 'cert' : (tmpIsPerProject ? 'perproject' : 'count'),
				Required: tmpRequired,
				Planned: tmpCounts.Planned,
				Created: tmpCreated,
				Passing: fNum(pPLIMT.PassingSampleCount),
				// No per-rule "CD received" flag in the Lite payload — use "a sample/cert exists" as the proxy for
				// on-file. (Refine once the live data exposes an explicit certificate status.)
				CertOnFile: (tmpCreated > 0) || (fNum(pPLIMT.CertificateRequired) === 1 && tmpCreated > 0),
			});
	});

	// --- rollups ---
	const tmpMaterialAgg = {};
	const tmpLineItemAgg = {};
	const tmpSectionAgg = {};
	Object.keys(tmpLineItems).forEach((pID) => { tmpLineItemAgg[pID] = fEmptyRollup(); });
	Object.keys(tmpSections).forEach((pCode) => { tmpSectionAgg[pCode] = Object.assign(fEmptyRollup(), { LineItems: {} }); });
	Object.keys(tmpMaterials).forEach((pKey) => { tmpMaterialAgg[pKey] = fEmptyRollup(); });
	const tmpPlanAgg = fEmptyRollup();
	tmpRules.forEach((pRule) =>
	{
		fAddRuleToRollup(tmpMaterialAgg[pRule.MaterialKey], pRule);
		if (tmpLineItemAgg[pRule.IDLineItem]) { fAddRuleToRollup(tmpLineItemAgg[pRule.IDLineItem], pRule); }
		const tmpSectionCode = tmpLineItems[pRule.IDLineItem].IDSection;
		if (tmpSectionAgg[tmpSectionCode]) { fAddRuleToRollup(tmpSectionAgg[tmpSectionCode], pRule); tmpSectionAgg[tmpSectionCode].LineItems[pRule.IDLineItem] = true; }
		fAddRuleToRollup(tmpPlanAgg, pRule);
	});

	// --- stamp display + rollup numbers ---
	const fStamp = (pRecord, pAgg) =>
	{
		pRecord.SamplesRequired = pAgg.SamplesRequired;
		pRecord.SamplesCreated = pAgg.SamplesCreated;
		pRecord.CertCount = pAgg.CertCount;
		pRecord.CertPending = pAgg.CertPending;
		pRecord.EntryCount = pAgg.Rules;
		pRecord.SatisfactionText = fRollupText(pAgg);
	};
	Object.values(tmpMaterials).forEach((pMat) => fStamp(pMat, tmpMaterialAgg[pMat.Key] || fEmptyRollup()));
	Object.values(tmpLineItems).forEach((pLI) => fStamp(pLI, tmpLineItemAgg[pLI.IDLineItem] || fEmptyRollup()));
	Object.values(tmpSections).forEach((pSec) =>
	{
		const tmpAgg = tmpSectionAgg[pSec.IDSection];
		pSec.LineItemCount = Object.keys(tmpAgg.LineItems).length;
		fStamp(pSec, tmpAgg);
		const tmpNumber = fStr(pSec.Section).trim();
		pSec.TreeLabel = (tmpNumber !== '') ? `${tmpNumber}  ·  ${pSec.Name}` : pSec.Name;
	});
	tmpRules.forEach((pRule) =>
	{
		pRule.SatisfactionText = (pRule.Kind === 'cert') ? 'By certificate'
			: (pRule.Kind === 'perproject') ? '1 / project'
			: `${pRule.Created}/${pRule.Required} samples`;
	});

	return {
		project: tmpPlan.Project || {},
		sections: Object.values(tmpSections).sort((a, b) => (String(a.Section) < String(b.Section) ? -1 : 1)),
		lineItems: Object.values(tmpLineItems).sort((a, b) => (a.Code < b.Code ? -1 : 1)),
		materials: Object.values(tmpMaterials),
		rules: tmpRules,
		plan: Object.assign({}, tmpPlanAgg, { SatisfactionText: fRollupText(tmpPlanAgg) }),
	};
};

/**
 * Recompute every satisfaction rollup (Material → LineItem → Section → plan) from the model's current rules, and
 * refresh each rule's SatisfactionText. Used after an in-place edit so the aggregates stay consistent with the changed
 * rule without a full reload. Mirrors the rollup+stamp pass in buildSamplePlanModel; TreeLabel/Name/counts of the
 * structural nodes are static (set once at build) and are left untouched.
 * @param {Object} pModel - the model returned by buildSamplePlanModel (mutated in place)
 * @returns {Object} the same model
 */
const restampRollups = (pModel) =>
{
	if (!pModel) { return pModel; }
	const tmpSections = pModel.sections || [];
	const tmpLineItems = pModel.lineItems || [];
	const tmpMaterials = pModel.materials || [];
	const tmpRules = pModel.rules || [];

	const tmpMaterialAgg = {};
	const tmpLineItemAgg = {};
	const tmpSectionAgg = {};
	const tmpSectionByLineItem = {};
	tmpMaterials.forEach((pMaterial) => { tmpMaterialAgg[pMaterial.Key] = fEmptyRollup(); });
	tmpLineItems.forEach((pLineItem) => { tmpLineItemAgg[pLineItem.IDLineItem] = fEmptyRollup(); tmpSectionByLineItem[pLineItem.IDLineItem] = pLineItem.IDSection; });
	tmpSections.forEach((pSection) => { tmpSectionAgg[pSection.IDSection] = Object.assign(fEmptyRollup(), { LineItems: {} }); });
	const tmpPlanAgg = fEmptyRollup();

	tmpRules.forEach((pRule) =>
	{
		if (tmpMaterialAgg[pRule.MaterialKey]) { fAddRuleToRollup(tmpMaterialAgg[pRule.MaterialKey], pRule); }
		if (tmpLineItemAgg[pRule.IDLineItem]) { fAddRuleToRollup(tmpLineItemAgg[pRule.IDLineItem], pRule); }
		const tmpSectionCode = tmpSectionByLineItem[pRule.IDLineItem];
		if (tmpSectionAgg[tmpSectionCode]) { fAddRuleToRollup(tmpSectionAgg[tmpSectionCode], pRule); tmpSectionAgg[tmpSectionCode].LineItems[pRule.IDLineItem] = true; }
		fAddRuleToRollup(tmpPlanAgg, pRule);
	});

	const fStamp = (pRecord, pAgg) =>
	{
		pRecord.SamplesRequired = pAgg.SamplesRequired;
		pRecord.SamplesCreated = pAgg.SamplesCreated;
		pRecord.CertCount = pAgg.CertCount;
		pRecord.CertPending = pAgg.CertPending;
		pRecord.EntryCount = pAgg.Rules;
		pRecord.SatisfactionText = fRollupText(pAgg);
	};
	tmpMaterials.forEach((pMaterial) => fStamp(pMaterial, tmpMaterialAgg[pMaterial.Key] || fEmptyRollup()));
	tmpLineItems.forEach((pLineItem) => fStamp(pLineItem, tmpLineItemAgg[pLineItem.IDLineItem] || fEmptyRollup()));
	tmpSections.forEach((pSection) =>
	{
		const tmpAgg = tmpSectionAgg[pSection.IDSection] || Object.assign(fEmptyRollup(), { LineItems: {} });
		pSection.LineItemCount = Object.keys(tmpAgg.LineItems).length;
		fStamp(pSection, tmpAgg);
	});
	tmpRules.forEach((pRule) =>
	{
		pRule.SatisfactionText = (pRule.Kind === 'cert') ? 'By certificate'
			: (pRule.Kind === 'perproject') ? '1 / project'
			: `${pRule.Created}/${pRule.Required} samples`;
	});
	pModel.plan = Object.assign({}, tmpPlanAgg, { SatisfactionText: fRollupText(tmpPlanAgg) });
	return pModel;
};

module.exports = { buildSamplePlanModel, restampRollups, resolvePlimtCounts };
